import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import SearchBar from 'shared/components/search-bar/search-bar';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import { SystemStatus } from 'features/systems/domain/models/system-status';
import { ReadIntercomIpRoomUnitsSystemResponse } from 'features/systems/ip-unit/domain/models/read-intercom-ip-room-units-response';
import useIPUnitRoomSystems from './hooks';
import styles from './ip-unit-room-systems.module.scss';

type Props = {
  gatewayId: string | undefined;
  isOpen: boolean;
  unavailableSystemIds: string[];
  onClose: () => void;
  onSystemSelected: (system: ReadIntercomIpRoomUnitsSystemResponse) => void;
};

const IPUnitRoomSystems = ({ gatewayId, isOpen, unavailableSystemIds, onClose, onSystemSelected }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('deviceDetails');
  const {
    systems,
    totalSystems,
    isLoading,
    searchQuery,
    selectedSystem,
    currentPage,
    currentRowsPerPage,
    handleOnClose,
    handleOnSearchValueChanged,
    clearSearch,
    handleOnSelect,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    setSelectedSystem,
  } = useIPUnitRoomSystems({ gatewayId, unavailableSystemIds, onClose, onSystemSelected });

  const getMonitoringType = (system: ReadIntercomIpRoomUnitsSystemResponse): MonitoringType => {
    switch (system.status) {
      case SystemStatus.offline:
        return MonitoringType.SystemOffline;
      case SystemStatus.warning:
        return MonitoringType.SystemWarning;
      case SystemStatus.malfunction:
        return MonitoringType.SystemMalfunction;
      case SystemStatus.online:
        return MonitoringType.SystemOnline;
      default:
        return MonitoringType.NotMonitored;
    }
  };

  const columns: ColumnDefinition<ReadIntercomIpRoomUnitsSystemResponse, NestedKeyof<ReadIntercomIpRoomUnitsSystemResponse>>[] = [
    { key: 'name', label: t('intercomMappingSystemModal.name'), disableSort: true },
    {
      key: 'status',
      label: t('intercomMappingSystemModal.type'),
      disableSort: true,
      renderCustomContentProvider: () => <Typography>{t('intercomMappingSystemModal.ipUnitType')}</Typography>,
    },
    {
      key: 'systemId',
      label: '',
      disableSort: true,
      renderCustomContentProvider: (system) =>
        system.status === SystemStatus.online ? <></> : <MonitoringIcon type={getMonitoringType(system)} withTooltip visible size={16} />,
      tableCellProps: { width: '24px' },
    },
  ];

  return (
    <Dialog
      className={styles.modal}
      open={isOpen}
      onClose={handleOnClose}
      slotProps={{ paper: { classes: { root: styles.paper }, sx: { overflow: 'visible' } } }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography variant="h2">{t('intercomMappingSystemModal.title')}</Typography>
          <Button variant="contained" startIcon={<CheckIcon />} disabled={!selectedSystem} onClick={handleOnSelect}>
            {t('intercomMappingSystemModal.linkButton')}
          </Button>
          <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.filters}>
          <SearchBar searchValue={searchQuery} onSearchValueChanged={handleOnSearchValueChanged} />
          <Button variant="text" color="primary" onClick={clearSearch}>
            {t('intercomMappingSystemModal.clearFilters')}
          </Button>
        </div>
        {isLoading && <LoadingIndicator />}
        {!isLoading && (
          <Table
            className={styles.table}
            columns={columns}
            data={systems.map<RowItem<ReadIntercomIpRoomUnitsSystemResponse>>((system) => ({ data: system }))}
            rowIdentifier={(system) => system.systemId}
            rowsAreSelectable
            selectedRows={selectedSystem ? [{ data: selectedSystem }] : []}
            onSelectedRowsChanged={(selectedSystems) => setSelectedSystem(selectedSystems.at(-1))}
            pagination={{
              count: totalSystems,
              page: currentPage,
              rowsPerPage: currentRowsPerPage,
              onPageChanged: handleOnPageChanged,
              onRowsPerPageChanged: handleOnRowsPerPageChanged,
            }}
          />
        )}
      </div>
    </Dialog>
  );
};

export default IPUnitRoomSystems;
